import type { FocusEventHandler} from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Stack from '@mui/material/Stack';
import type { Theme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { getCloudinaryImageInfo, parsePublicId, uploadToCloudinary } from 'utils/cloudinary';
import ImageIcon from './vectors/ImageIcon';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Zoom from '@mui/material/Zoom';
import FormHelperText from '@mui/material/FormHelperText';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import { P, match } from 'ts-pattern';
import CircularProgress from '@mui/material/CircularProgress';
import { bytesToMB } from 'utils/base64';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import useMediaQuery from '@mui/material/useMediaQuery';
import BadgeIconButton from 'components/BadgeIconButton';
import CrossIcon from 'components/vectors/CrossIcon';
import Skeleton from '@mui/material/Skeleton';
import { Image } from 'components/image';
import { useSnackbar } from 'notistack';

const Container = styled(ButtonBase)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: theme.spacing(2),
  backgroundColor: theme.palette.secondary.variant,
  borderRadius: '10px',
  padding: '10px 16px 10px 10px',

  '&.Mui-disabled': {
    opacity: theme.palette.action.disabledOpacity,
  },
  '&:hover, &:focus': {
    background: `linear-gradient(0deg, ${theme.palette.action.secondaryHover} 0%, ${theme.palette.action.secondaryHover} 100%), ${theme.palette.secondary.variant}`,
  },
}));

const ImageHolder = styled('div', {
  shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.outline.medium}`,
  backgroundColor: theme.palette.secondary.main,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: theme.spacing(12),
  height: theme.spacing(8),
  '& > img': {
    objectFit: 'cover',
  },
  [theme.breakpoints.down('sm')]: {
    width: '90px',
    height: '60px',
  },

  '&.variant-seo': {
    width: '244px',
    height: '128px',
    backgroundColor: theme.palette.background.secondaryVariant,
    border: 'none',
    '&:hover, &:focus': {
      background: `linear-gradient(0deg, ${theme.palette.action.secondaryHover} 0%, ${theme.palette.action.secondaryHover} 100%), ${theme.palette.background.secondaryVariant}`,
    },
  },
}));

interface ImageMetadata {
  name: string;
  width: number;
  height: number;
  bytes: number;
}

interface Props {
  name?: string;
  value?: string | null;
  accept?: 'image/*' | 'video/*';
  onChange: (value: string | null) => void;
  label?: string;
  helperText?: string;
  multiple?: boolean;
  disabled?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  error?: boolean;
  variant?: 'default' | 'seo';
}

export default function ImageUploadField(props: Props) {
  const {
    value,
    name,
    accept = 'image/*',
    onChange,
    label,
    helperText,
    multiple = false,
    disabled = false,
    onBlur,
    error,
    variant = 'default',
  } = props;
  const isTablet = useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'));

  const [isLoading, setIsLoading] = useState(false);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [metadata, setMetadata] = useState<ImageMetadata>();
  const [showRemove, setShowRemove] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.type.split('/')[0] !== accept.split('/')[0]) {
        enqueueSnackbar('Invalid file type', { variant: 'error' });
        return;
      }
      setIsLoading(true);
      uploadToCloudinary(file)
        .then(({ url }) => onChange(url))
        .finally(() => setIsLoading(false));
    }
  }, []);

  useEffect(() => {
    if (value) {
      const publicId = parsePublicId(value);
      setIsMetadataLoading(true);
      getCloudinaryImageInfo(publicId)
        .then((info) => {
          info &&
            setMetadata({
              name: publicId,
              width: info.output.width,
              height: info.output.height,
              bytes: info.output.bytes,
            });
        })
        .finally(() => setIsMetadataLoading(false));
    } else {
      setMetadata(undefined);
    }

    return () => {
      setMetadata(undefined);
    };
  }, [value]);

  const imageInputRenderer = (
    <input
      name={name}
      multiple={multiple}
      id="image-uploader"
      hidden
      type="file"
      accept={accept}
      onChange={handleUpload}
      onBlur={onBlur}
    />
  );

  const imageRenderer = useMemo(
    () =>
      match([isLoading, value])
        .with([true, P.any], () => (
          <ImageHolder className={`variant-${variant}`}>
            <CircularProgress size={24} />
          </ImageHolder>
        ))
        .with([false, P.string], () => (
          <Badge
            overlap="rectangular"
            onMouseEnter={() => setShowRemove(true)}
            onMouseLeave={() => setShowRemove(false)}
            slotProps={{
              badge: {
                style: {
                  padding: 0,
                  height: 'unset',
                  lineHeight: 'unset',
                  transform: 'scale(1) translate(5px, -5px)',
                },
              },
            }}
            badgeContent={
              <Zoom in={showRemove || isTablet}>
                <BadgeIconButton
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    onChange(null);
                    setMetadata(undefined);
                  }}
                >
                  <CrossIcon sx={{ m: '-2px' }} />
                </BadgeIconButton>
              </Zoom>
            }
          >
            <ImageHolder className={`variant-${variant}`}>
              <Image src={value as string} alt={name || 'image'} />
            </ImageHolder>
          </Badge>
        ))
        .otherwise(() => (
          <ImageHolder className={`variant-${variant}`}>
            <ImageIcon color="action" sx={{ opacity: 0.5 }} />
          </ImageHolder>
        )),
    [isLoading, value, name, onChange, showRemove, isTablet]
  );

  const labelRenderer = useMemo(
    () =>
      match(isMetadataLoading)
        .with(true, () => <Skeleton width={200} sx={{ maxWidth: '100%' }} />)
        .otherwise(() => {
          const defaultText = variant === 'seo' ? 'Social Preview' : 'Choose file...';

          return (value && metadata?.name) || defaultText;
        }),
    [isMetadataLoading, metadata?.name, value, variant]
  );

  const descriptionRenderer = useMemo(
    () =>
      match(isMetadataLoading)
        .with(true, () => <Skeleton width={120} />)
        .otherwise(() => {
          const defaultText =
            variant === 'seo' ? 'Recommended size 1200 x 630' : 'Recommended size 1200 x 700';

          return value && metadata
            ? `${metadata.width} x ${metadata.height} · ${bytesToMB(metadata.bytes).toFixed(2)} MB`
            : defaultText;
        }),
    [isMetadataLoading, metadata?.width, metadata?.height, metadata?.bytes, value, variant]
  );

  const actionButton = useMemo(() => {
    if (variant === 'seo') {
      return (
        <Button
          component="label"
          htmlFor="image-uploader"
          variant="outlined"
          size="small"
          disabled={disabled}
          sx={{ mr: 'auto', mt: 3.5 }}
          className="replace-button"
        >
          {value ? 'Replace' : 'Upload'}
        </Button>
      );
    }

    return value ? (
      <Button
        component="label"
        htmlFor="image-uploader"
        variant="outlined"
        size="small"
        disabled={disabled}
        sx={{ ml: 'auto' }}
        className="replace-button"
      >
        Replace
      </Button>
    ) : null;
  }, [value, disabled, variant]);

  if (variant === 'seo') {
    return (
      <FormControl disabled={disabled} fullWidth error={error}>
        {label ? <InputLabel sx={{ mb: '4px' }}>{label}</InputLabel> : null}
        {helperText ? <FormHelperText sx={{ mb: '12px' }}>{helperText}</FormHelperText> : null}
        <Container
          disabled={disabled}
          /** @ts-ignore */
          component="label"
          htmlFor="image-uploader"
          disableRipple={false}
          sx={{
            p: 0,
            justifyContent: 'space-between',
            gap: 4,
            backgroundColor: 'transparent',
            '&:hover, &:focus': {
              background: 'none',
            },
          }}
        >
          {imageInputRenderer}
          <Stack
            flexDirection="column"
            gap="4px"
            alignItems="flex-start"
            maxWidth="calc(100% - 280px)"
          >
            <Typography
              variant="body2"
              color="secondary.high"
              noWrap
              width="100%"
              fontWeight="medium"
            >
              {labelRenderer}
            </Typography>
            <Typography variant="body2" color="secondary.low">
              {descriptionRenderer}
            </Typography>
            {actionButton}
          </Stack>

          {imageRenderer}
        </Container>
      </FormControl>
    );
  }

  return (
    <FormControl disabled={disabled} fullWidth error={error}>
      {label ? <InputLabel sx={{ mb: '4px' }}>{label}</InputLabel> : null}
      {helperText ? <FormHelperText sx={{ mb: '12px' }}>{helperText}</FormHelperText> : null}
      <Container
        disabled={disabled}
        /** @ts-ignore */
        component="label"
        htmlFor="image-uploader"
        disableRipple={false}
      >
        {imageInputRenderer}
        {imageRenderer}
        <Stack
          flexDirection="column"
          gap="4px"
          alignItems="flex-start"
          maxWidth="calc(100% - 196px)"
        >
          <Typography variant="body2" color="secondary.high" noWrap width="100%">
            {labelRenderer}
          </Typography>
          <Typography variant="caption" color="secondary.low">
            {descriptionRenderer}
          </Typography>
        </Stack>

        {actionButton}
      </Container>
    </FormControl>
  );
}
